import pc from 'picocolors';

export interface OutputOptions {
  verbose?: boolean;
  color?: boolean;
}

export class OutputManager {
  private options: OutputOptions;

  constructor(options: OutputOptions = {}) {
    this.options = {
      verbose: false,
      color: true,
      ...options
    };
  }

  private formatMessage(message: string, color?: (text: string) => string): string {
    if (!this.options.color || !color) {
      return message;
    }
    return color(message);
  }

  success(message: string): void {
    console.log(this.formatMessage(`✓ ${message}`, pc.green));
  }

  error(message: string): void {
    console.error(this.formatMessage(`✗ ${message}`, pc.red));
  }

  warning(message: string): void {
    console.log(this.formatMessage(`⚠ ${message}`, pc.yellow));
  }

  info(message: string): void {
    console.log(this.formatMessage(`ℹ ${message}`, pc.cyan));
  }

  debug(message: string): void {
    if (this.options.verbose) {
      console.log(this.formatMessage(`[DEBUG] ${message}`, pc.dim));
    }
  }

  header(message: string): void {
    console.log('');
    console.log(this.formatMessage(message, pc.bold));
    console.log('');
  }

  section(title: string): void {
    console.log('');
    console.log(this.formatMessage(title, pc.underline));
  }

  list(items: string[], bullet = '•'): void {
    items.forEach(item => {
      console.log(`  ${this.formatMessage(bullet, pc.dim)} ${item}`);
    });
  }

  table(data: Record<string, string | number | boolean>): void {
    const maxKeyLength = Math.max(...Object.keys(data).map(k => k.length));
    
    Object.entries(data).forEach(([key, value]) => {
      const paddedKey = key.padEnd(maxKeyLength);
      const formattedKey = this.formatMessage(paddedKey, pc.cyan);
      console.log(`  ${formattedKey} : ${value}`);
    });
  }

  banner(text: string, emoji = '🚀'): void {
    console.log('');
    console.log(this.formatMessage(`${emoji} ${text}`, pc.bold));
    console.log('');
  }

  separator(): void {
    console.log(this.formatMessage('─'.repeat(50), pc.dim));
  }
}

export function createOutputManager(options: OutputOptions = {}): OutputManager {
  return new OutputManager(options);
}

// Global singleton for convenience
let globalOutput: OutputManager | null = null;

export function getOutput(options?: OutputOptions): OutputManager {
  if (!globalOutput || options) {
    globalOutput = new OutputManager(options);
  }
  return globalOutput;
}